import { useEffect } from 'react'
import { ResponseVariant } from 'workflow/Insights/Insights'

import Button from 'common/components/button'
import Loader from 'common/components/loaders/Loader'
import TableWidget from 'common/components/widget/tableWidget'
import { CurrentUsersBanner } from 'features/unifiedFlow/contentPage/components/CurrentUsersBanner'

import { useSelectedComponentElement } from '../../hooks'
import {
  ResponseVariantExtended,
  useManageOptimizeVariants,
} from '../hooks/useManageOptimizeVariants'
import { useManagePersonalizeVariants } from '../hooks/useManagePersonalizeVariants'

import { useManageTableColumns } from './useManageTableColumns'
import { VariantStatus } from './VariantStatusFilters'

type Props = {
  tweakSuccessVariantIds: string[]
  onSelectVariant: (variant: ResponseVariant) => void
  statusFilter: VariantStatus
  selectedPersonalizationId: string | undefined
  onStatusCountsChange?: (counts: {
    pending: number
    live: number
    dropped: number
  }) => void
}

const ManageApprovalsTable = ({
  tweakSuccessVariantIds,
  onSelectVariant,
  statusFilter,
  selectedPersonalizationId,
  onStatusCountsChange,
}: Props) => {
  const { data: selectedElement } = useSelectedComponentElement()
  const personalizeHook = useManagePersonalizeVariants({
    statusFilter,
    onSelectVariant,
    tweakSuccessVariantIds,
    selectedPersonalizationId,
  })
  const optimizeHook = useManageOptimizeVariants({
    statusFilter,
    onSelectVariant,
  })
  const isPersonalize = !!selectedElement?.personalization_id
  const hook = isPersonalize ? personalizeHook : optimizeHook
  const {
    selectedVariantIds,
    approvingVariantIds,
    droppingVariantIds,
    variantsToBeApprovedFiltered,
    isLoading,
    handleRowClick,
    handleCheckboxClick,
    handleSelectAll,
    handleApproveVariant,
    handleDropVariant,
    handleBulkApprove,
    handleBulkDrop,
  } = hook
  const statusCounts = 'statusCounts' in hook ? hook.statusCounts : undefined

  useEffect(() => {
    if (statusCounts && onStatusCountsChange) {
      onStatusCountsChange(statusCounts)
    }
  }, [statusCounts, onStatusCountsChange])

  const columns = useManageTableColumns({
    selectedVariantIds,
    variantsToBeApprovedFiltered,
    approvingVariantIds,
    droppingVariantIds,
    statusFilter,
    handleSelectAll,
    handleRowClick,
    handleCheckboxClick,
    handleApproveVariant,
    handleDropVariant,
  })

  if (!selectedElement) {
    return null
  }

  if (isLoading) {
    return <Loader className="m-auto" />
  }

  return (
    <div className="mb-16">
      <CurrentUsersBanner />
      <div className="relative">
        <TableWidget.Widget<ResponseVariantExtended>
          key={`${selectedElement.element_id}-${statusFilter}`}
          className="!border-0 !shadow-none"
          data={variantsToBeApprovedFiltered}
          columns={columns}
          onRowClick={handleRowClick}
          showNoData={false}
          generateRowClassName={({ row }) => {
            const variant = row.original as ResponseVariantExtended
            const id = variant.variant_id || variant._id
            const isSelected = selectedVariantIds.includes(id)
            const isLoading = variant.isLoading

            if (isLoading) {
              return 'bg-gold-50 bg-opacity-75 hover:bg-gold-50'
            }
            if (isSelected) {
              return 'bg-pink-100 hover:bg-pink-200'
            }
            return ''
          }}
          autoResetPage={false}
          disablePagination={false}
          initialState={{
            pageSize: 20,
            pageIndex: 0,
          }}
        >
          <div className="absolute -top-16 right-0 z-10">
            <TableWidget.Filter placeholder="Search variants..." />
          </div>
          {statusFilter === 'live' && (
            <TableWidget.ActionButtons key="drop-actions">
              <Button
                variant="primary"
                onClick={handleBulkDrop}
                disabled={selectedVariantIds.length === 0}
              >
                Drop
                {selectedVariantIds.length > 0 &&
                  ` (${selectedVariantIds.length})`}
              </Button>
            </TableWidget.ActionButtons>
          )}
          {statusFilter !== 'live' && statusFilter !== 'dropped' && (
            <TableWidget.ActionButtons key="approve-actions">
              <Button
                variant="primary"
                onClick={handleBulkApprove}
                disabled={selectedVariantIds.length === 0}
              >
                Approve
                {selectedVariantIds.length > 0 &&
                  ` (${selectedVariantIds.length})`}
              </Button>
            </TableWidget.ActionButtons>
          )}
        </TableWidget.Widget>
      </div>
    </div>
  )
}

export default ManageApprovalsTable
